import { Component, OnInit } from '@angular/core';

import { AddressValidationUploadModel } from '../../pathways-ui/models/address-validation-upload.model';

import { SpBackofficeService } from 'src/app/services/sp-backoffice-service';
import { SpConsentService } from 'src/app/services/sp-consent.service';
import { SpRegisterService } from 'src/app/services/sp-register.service';
import { TranslateService } from '@ngx-translate/core';
import { BackofficeService } from 'src/app/pathways-ui/services/backoffice.service';
import { ConsentComplete } from 'src/app/pathways-ui/models/consent-completed.model';
import { GuardianModel } from 'src/app/pathways-ui/models/guardian-model';
import { InitiateSigningModel } from 'src/app/pathways-ui/models/initate-signing.model';
import { ReferenceTableService } from 'src/app/pathways-ui/services/reference-table.service';
import { RegistrationSchoolYearModel } from 'src/app/pathways-ui/models/registration-schoolyear.model';
import { SettingsService } from 'src/app/pathways-ui/services/settings.service';
import { StudentAccountStatus } from 'src/app/pathways-ui/models/student-account-status.model';
import { StudentConsentModel } from 'src/app/pathways-ui/models/student-consent.model';
import { StudentConsentStatusCodes } from 'src/app/pathways-ui/models/consent-status.model';
import { StudentProfile } from 'src/app/pathways-ui/models/student-profile.model';
import { StudentRegistrationStatusCodes } from 'src/app/pathways-ui/models/StudentFormStatusCodes.enum';
import { UpdateStudentStatusModel } from 'src/app/pathways-ui/models/update-student-status.model';
import { StudentPortalService } from 'src/app/services/student-portal.service';

@Component({
    selector: 'app-profile-view',
    templateUrl: './student-profile-view.component.html',
    styleUrls: [
        './student-profile-view.component.scss',
        './student-profile.scss',
    ],
})
export class StudentProfileViewComponent implements OnInit {
    studentProfile: StudentProfile | undefined;
    studentConsents: StudentConsentModel[] = [];
    consentLink: StudentConsentModel | undefined;
    addressValidation: boolean = false;
    panelOpenState = false;

    constructor(
        private _spBoService: SpBackofficeService,
        private _spService: StudentPortalService,
        private _boService: BackofficeService,
        private _spConsentService: SpConsentService,
        private _registerService: SpRegisterService,
        private _settingsService: SettingsService,
        public _translator: TranslateService,
        public _references: ReferenceTableService
    ) {
        /// assumes user is created at this point let's set this to true
        this._spService.studentByEmailExecuted.next(true);
    }

    StudentRegistrationStatusCodes = StudentRegistrationStatusCodes;

    // Load student profile on init
    async ngOnInit(): Promise<void> {
        this.fetchStudentProfile();
    }

    // Get General Section Completion
    get isGeneralSectionComplete() {
        if (!this.studentProfile) {
            return true;
        }

        // address
        if (
            !this.studentProfile.address ||
            !this.studentProfile.city ||
            !this.studentProfile.provinceId ||
            !this.studentProfile.postCode
        ) {
            return false;
        }

        if (!this.studentProfile.email) {
            return false;
        }

        if (!this.studentProfile.cellPhone) {
            return false;
        }

        return true;
    }

    // Get Guardian Section Completion
    get isGuardianSectionComplete() {
        if (!this.studentProfile) {
            return true;
        }

        if (!this.studentProfile.guardians.length) {
            return false;
        }

        return true;
    }

    // Get Demographic Section Completion
    get isDemographicsSectionComplete() {
        if (!this.studentProfile) {
            return true;
        }

        if (!this.studentProfile.demographic) {
            return false;
        }

        if (!this.studentProfile.demographic.races.length) {
            return false;
        }

        if (
            !this.studentProfile.demographic.indigenous.length &&
            !this.studentProfile.demographic.indigenousSpecification
        ) {
            return false;
        }

        if (!this.studentProfile.demographic.countryOfBirthId) {
            return false;
        }

        if (
            this.studentProfile.demographic.countryOfBirthId !==
                this.canadaCountryId &&
            !this.studentProfile.demographic.arriveDate
        ) {
            return false;
        }

        return true;
    }

    // Get School Section Completion
    get isSchoolSectionComplete() {
        if (!this.studentProfile) {
            return true;
        }

        if (!this.studentProfile.registrationSchoolYear.gradeId) {
            return false;
        }

        if (!this.studentProfile.registrationSchoolYear.schoolId) {
            return false;
        }
        if (!this.studentProfile.registrationSchoolYear.previousSchoolId) {
            return false;
        }
        if (!this.studentProfile.registrationSchoolYear.designationId) {
            return false;
        }
        return true;
    }

    // Get Consent Section Completion
    get isConsentSectionComplete() {
        if (
            this.studentConsents.some(
                (c) => c.statusId === StudentConsentStatusCodes.Incomplete
            )
        ) {
            return false;
        }

        if (
            this.consentLink &&
            this.consentLink.statusId === StudentConsentStatusCodes.Incomplete
        ) {
            return false;
        }

        return true;
    }

    // Get Submit Status
    get isSubmitDisabled() {
        if (
            !this.isConsentSectionComplete ||
            !this.isDemographicsSectionComplete ||
            !this.isSchoolSectionComplete ||
            !this.isAddressValidationSectionComplete ||
            !this.isGeneralSectionComplete ||
            !this.isGuardianSectionComplete
        ) {
            return true;
        }

        return false;
    }

    // Get Address Validation Section
    get isAddressValidationSectionComplete() {
        if (!this.addressValidation) {
            return true;
        }

        if (
            !this.studentProfile.registrationSchoolYear.addressValidationFileId
        ) {
            return false;
        }

        return true;
    }

    // Get Canada Country ID
    get canadaCountryId() {
        if (!this._references.canadaCountryId) {
            return 0;
        }

        return this._references.canadaCountryId;
    }

    // Get Submission Completion Status
    get isSubmissionComplete() {
        if (!this.studentProfile) {
            return false;
        }

        if (
            this.studentProfile.registrationSchoolYear.statusId ===
                this.StudentRegistrationStatusCodes.RegistrationSubmitted ||
            this.studentProfile.registrationSchoolYear.statusId ===
                this.StudentRegistrationStatusCodes.ReRegistrationSubmitted
        ) {
            return true;
        }

        return false;
    }

    // Get Reregistration Submission Status
    get isReRegistrationSubmitted() {
        return (
            this.studentProfile.registrationSchoolYear.statusId ===
            this.StudentRegistrationStatusCodes.ReRegistrationSubmitted
        );
    }

    fetchStudentProfile() {
        this._spService.getStudentProfile().subscribe((val) => {
            this.studentProfile = val;
            this.checkStudentAccountStatus();
            this.fetchOrgSettings();
            //this.fetchConsentLinkForOrg();
            //this.fetchStudentConsents();
        });
    }

    // Fetch Organization Settings
    fetchOrgSettings() {
        this._settingsService
            .getSettingsInStudentPortal(this.studentProfile.locationId)
            .subscribe((val: any) => {
                this.addressValidation = val.addressValidation;
                console.log('address val', this.addressValidation);
            });
    }

    // Fetch Consent Link for Organization
    fetchConsentLinkForOrg() {
        this._spConsentService
            .getConsentLink(
                this.studentProfile.registrationSchoolYear
                    .studentRegistrationSchoolYearId
            )
            .subscribe((val) => {
                this.consentLink = val;
            });
    }

    // Fetch Student Consent Form
    fetchStudentConsents() {
        this._spConsentService
            .getStudentConsentsToSign(
                this.studentProfile.registrationSchoolYear
                    .studentRegistrationSchoolYearId
            )
            .subscribe((val) => {
                this.studentConsents = val;
            });
    }

    // Check Student Account Status - Is the account created?
    checkStudentAccountStatus() {
        if (
            this.studentProfile.statusId !== StudentAccountStatus.AccountCreated
        ) {
            this.setStudentStatusToCreatedAccount();
        }
    }

    // Sets student status to created account
    setStudentStatusToCreatedAccount() {
        let statusUpdate = new UpdateStudentStatusModel();
        statusUpdate.status = StudentAccountStatus.AccountCreated;
        statusUpdate.studentId = this.studentProfile.studentId;
        this._registerService.updateStudentStatus(statusUpdate).subscribe();
    }

    onSubmitClick() {
        const statusToSet =
            this.studentProfile.registrationSchoolYear.statusId ===
            this.StudentRegistrationStatusCodes.RegistrationDraft
                ? this.StudentRegistrationStatusCodes.RegistrationSubmitted
                : this.StudentRegistrationStatusCodes.ReRegistrationSubmitted;
        this._registerService
            .updateSingleRegistrationStatus(statusToSet)
            .subscribe((val) => {
                this.fetchStudentProfile();
            });
    }

    onDownloadSignedClick(fileId: string) {
        this._boService
            .downloadSignedConsentForm(fileId)
            .subscribe((val: Blob | MediaSource) => {
                console.log('VAL', val);
                const fileURL = URL.createObjectURL(val);
                window.open(fileURL, '_blank');
            });
    }

    onDownloadUnSignedClick(consentId: number) {
        this._boService
            .downloadConsentForm(consentId)
            .subscribe((res: Blob | MediaSource) => {
                const fileURL = URL.createObjectURL(res);
                window.open(fileURL, '_blank');
            });
    }

    onSignConsent(consent: StudentConsentModel) {
        let initSigning = new InitiateSigningModel();
        initSigning.contact = this.studentProfile.guardians[0]
            ? this.studentProfile.guardians[0].email
            : '';
        initSigning.studentConsentId = consent.studentConsentId;
        initSigning.consentId = consent.consentId;
        initSigning.studentId = this.studentProfile.studentId;
        this._spConsentService
            .signSingleDocument(initSigning)
            .subscribe((val: any) => {
                if (val && val.url) {
                    window.location = val.url;
                }
            });
    }

    downloadAddressValidation() {
        this._boService
            .downloadAddressValidationPhoto(
                this.studentProfile.registrationSchoolYear
                    .studentRegistrationSchoolYearId
            )
            .subscribe((val: any) => {
                const fileURL = URL.createObjectURL(val);
                window.open(fileURL, '_blank');
            });
    }

    onFileUpload({ bytes, type }: any) {
        let addressVal = new AddressValidationUploadModel();
        addressVal.photoBase64Content = bytes.split(',')[1]; // remove data:image/*;base64 part
        addressVal.contentType = type;
        addressVal.studentRegistrationSchoolYearId =
            this.studentProfile.registrationSchoolYear.studentRegistrationSchoolYearId;
        addressVal.photoGuid =
            this.studentProfile.registrationSchoolYear.addressValidationFileId;

        this._boService
            .uploadAddressValidationPhoto(addressVal)
            .subscribe((val: any) => {
                if (val) {
                    this.studentProfile.registrationSchoolYear.addressValidationFileId =
                        val.photoGuid;
                }
            });
    }

    onConsentLinkClick(consent: StudentConsentModel) {
        if (
            consent.link.includes('https://') ||
            consent.link.includes('http://')
        ) {
            window.open(consent.link, '_blank');
        } else {
            window.open('//' + consent.link, '_blank');
        }
        let consentData = new ConsentComplete();
        consentData.studentConsentId = consent.studentConsentId;
        consentData.studentId = this.studentProfile.studentId;
        this._registerService
            .updateStudentConsentLink(consentData)
            .subscribe(() => {
                consent.statusId = StudentConsentStatusCodes.Complete;
            });
    }

    updateStudentProfile(student: StudentProfile) {
        this.fetchStudentConsents();
        this.studentProfile = student;
        if (student.guardians.some((i: any) => i.addressSameAsStudent)) {
            this.updateGuardianAddresses(
                student.guardians.filter((i: any) => i.addressSameAsStudent)
            );
        }

        if (this.studentProfile.demographic) {
            this.studentProfile.registrationSchoolYear =
                this.updateRegistrationSchoolYearFromDemographic(student);
        }
    }

    updateGuardianAddresses(guardians: GuardianModel[]) {
        for (let guardian of guardians) {
            guardian.address = this.studentProfile.address;
            guardian.unitNum = this.studentProfile.unitNum;
            guardian.postCode = this.studentProfile.postCode;
            guardian.city = this.studentProfile.city;
            guardian.provinceId = this.studentProfile.provinceId;
        }
    }

    updateRegistrationSchoolYearFromDemographic(
        student: StudentProfile
    ): RegistrationSchoolYearModel {
        let schoolYearRegistration = new RegistrationSchoolYearModel();
        schoolYearRegistration.studentRegistrationSchoolYearId =
            student.registrationSchoolYear.studentRegistrationSchoolYearId;
        schoolYearRegistration.accommodation =
            student.demographic.accommodation;

        schoolYearRegistration.designationId =
            student.demographic.designationId;

        schoolYearRegistration.gradeId = student.demographic.gradeId;

        schoolYearRegistration.previousSchoolId =
            student.demographic.previousSchoolId;

        schoolYearRegistration.previousSchoolName =
            student.demographic.previousSchoolName;

        schoolYearRegistration.schoolBoardId =
            student.demographic.schoolBoardId;

        schoolYearRegistration.schoolId = student.demographic.schoolId;

        schoolYearRegistration.schoolName = student.demographic.schoolName;

        schoolYearRegistration.statusId =
            student.registrationSchoolYear.statusId;

        schoolYearRegistration.streamId = student.demographic.streamId;
        schoolYearRegistration.addressValidationContentType =
            student.registrationSchoolYear.addressValidationContentType;
        schoolYearRegistration.addressValidationFileId =
            student.registrationSchoolYear.addressValidationFileId;

        return schoolYearRegistration;
    }
}
